import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { useContainerWidth, useMenuOverlay, useOverlay } from '@fs/zion-ui'
import {
  artifactManagerService,
  getUserMemories,
  memoryUtils,
  sessionKeys,
} from '@fs/zion-memories/memories-authorized-use-only'
import { useEffectiveUser } from '@fs/zion-user'
import { useFeatureFlags } from '@fs/zion-flags'
import { useHistory } from '@fs/zion-router'
import ReportAbuseOverlay from '@fs/zion-report-abuse-overlay'
import { AlbumsProvider } from './AlbumsContext'
import MemoriesProviderWrapper from './MemoriesProviderWrapper'
import {
  filterOutMemories,
  filterOutMemory,
  convertObjectsToIds,
  isAlbumPath,
  paths,
  getPathFromUri,
  removeTrailingSlash,
} from './galleryUtils'

const GalleryContext = createContext()
const GalleryApiContext = createContext()

const galleryActionTypes = {
  beginEditing: 'BEGIN_EDITING',
  endEditing: 'END_EDITING',
  init: 'INIT',
  removeMemories: 'REMOVE_MEMORIES',
  setActivePage: 'SET_ACTIVE_PAGE',
  setArchivedMemories: 'SET_ARCHIVED_MEMORIES',
  setBookmarkedMemories: 'SET_BOOKMARKED_MEMORIES',
  setDisplayType: 'SET_DISPLAY_TYPE',
  setMemoriesDirty: 'SET_MEMORIES_DIRTY',
  setMemoriesErrors: 'SET_MEMORIES_ERRORS',
  setMemoriesLoading: 'SET_MEMORIES_LOADING',
  setPendingAction: 'SET_PENDING_ACTION',
  setRecentlyDeletedMemories: 'SET_RECENTLY_DELETED_MEMORIES',
  setRestoredMemories: 'SET_RESTORED_MEMORIES',
  setSelectedIds: 'SET_SELECTED_IDS',
  setSelectMode: 'SET_SELECT_MODE',
  setShowExplorer: 'SET_SHOW_EXPLORER',
  setSortAscending: 'SET_SORT_ASCENDING',
  setSortingType: 'SET_SORTING_FUNCTION',
  setTombstones: 'SET_TOMBSTONES',
  setUserMemories: 'SET_USER_MEMORIES',
  updateMemories: 'UPDATE_MEMORIES',
  updateMemory: 'UPDATE_MEMORY',
}

const collections = {
  albums: 'albums',
  archive: 'archive',
  bookmarkedAlbums: 'bookmarkedAlbums',
  bookmarks: 'bookmarks',
  deleted: 'deleted',
  memories: 'memories',
  missing: 'missing',
}

const displayTypes = {
  grid: 'grid',
  list: 'list',
}

const sortingTypes = {
  custom: 'custom',
  dateAdded: 'dateAdded',
  dateOfMemory: 'dateOfMemory',
  title: 'title',
  place: 'place',
}

const isMemoryError = (index, memoriesResponses) =>
  memoriesResponses?.[index]?.status === 'rejected' || memoriesResponses?.[index]?.value?.memoryError

const replaceMemory = (updatedMemory, memories) => {
  const memoryToReplace = memories.findIndex((memory) => memory?.id === updatedMemory?.id)
  const updatedMemories = [...memories]
  updatedMemories[memoryToReplace] = updatedMemory
  return updatedMemories
}

const galleryReducer = (state, action) => {
  switch (action.type) {
    case galleryActionTypes.init:
      return {
        ...state,
        archivedMemories: action.archivedMemories,
        bookmarkedMemories: action.bookmarkedMemories,
        missingMemories: action.missingMemories,
        recentlyDeletedMemories: action.recentlyDeletedMemories,
        tombstones: action.tombstones,
        userMemories: action.userMemories,
        memoriesErrors: action.memoriesErrors,
        memoriesLoading: false,
      }
    case galleryActionTypes.addMemories: {
      return { ...state, userMemories: [...state.userMemories, ...action.memories] }
    }
    case galleryActionTypes.removeMemories: {
      const recentlyDeletedMemories = [...state.recentlyDeletedMemories, ...action.memories]
      const deletedIds = convertObjectsToIds(action.memories)
      const bookmarkedMemories = filterOutMemories(state.bookmarkedMemories, deletedIds)
      const newState = { ...state, bookmarkedMemories, recentlyDeletedMemories }

      if (action.memories?.[0]?.archived) {
        return {
          ...newState,
          archivedMemories: filterOutMemories(state.archivedMemories, deletedIds),
          memoriesDirty: true,
        }
      }

      return {
        ...newState,
        userMemories: filterOutMemories(state.userMemories, deletedIds),
        memoriesDirty: true,
      }
    }
    case galleryActionTypes.setShowExplorer:
      return { ...state, showExplorer: action.showExplorer }
    case galleryActionTypes.setMemoriesLoading:
      return { ...state, memoriesLoading: action.memoriesLoading }
    case galleryActionTypes.setMemoriesDirty:
      return { ...state, memoriesDirty: action.memoriesDirty }
    case galleryActionTypes.setMemoriesErrors:
      return { ...state, memoriesErrors: [...state.memoriesErrors, ...action.memoriesErrors] }
    case galleryActionTypes.setDisplayType: {
      sessionStorage.setItem(sessionKeys.galleryView, action.displayType)
      return { ...state, displayType: action.displayType }
    }
    case galleryActionTypes.setSelectMode:
      return { ...state, pendingAction: false, selectMode: action.selectMode, selectedIds: [] }
    case galleryActionTypes.setSelectedIds:
      return { ...state, selectedIds: action.selectedIds }
    case galleryActionTypes.setSortingType:
      return { ...state, sortingType: action.sortingType }
    case galleryActionTypes.setSortAscending:
      return { ...state, sortAscending: action.sortAscending }
    case galleryActionTypes.setActivePage: {
      const { path } = action
      let activePage = getPathFromUri(path)

      if (isAlbumPath(path)) {
        activePage = paths.album
      }

      activePage = removeTrailingSlash(activePage)
      return { ...state, activePage }
    }
    case galleryActionTypes.setArchivedMemories:
      return { ...state, archivedMemories: action.archivedMemories }
    case galleryActionTypes.setBookmarkedMemories:
      return { ...state, bookmarkedMemories: action.bookmarkedMemories }
    case galleryActionTypes.setPendingAction:
      return { ...state, pendingAction: action.pendingAction }
    case galleryActionTypes.setRecentlyDeletedMemories:
      return { ...state, recentlyDeletedMemories: action.recentlyDeletedMemories }
    case galleryActionTypes.setRestoredMemories:
      return {
        ...state,
        tombstones: action.tombstones,
        recentlyDeletedMemories: action.recentlyDeletedMemories,
        userMemories: [...state.userMemories, ...action.userMemories],
      }
    case galleryActionTypes.setTombstones:
      return { ...state, tombstones: action.tombstones }
    case galleryActionTypes.setUserMemories:
      return { ...state, userMemories: action.userMemories }
    case galleryActionTypes.beginEditing: {
      state.actionsMenuOverlay.close()
      return { ...state, editing: true }
    }
    case galleryActionTypes.endEditing:
      return { ...state, editing: false }
    case galleryActionTypes.updateMemory:
      return {
        ...state,
        archivedMemories: replaceMemory(action.memory, state.archivedMemories),
        bookmarkedMemories: replaceMemory(action.memory, state.bookmarkedMemories),
        recentlyDeletedMemories: replaceMemory(action.memory, state.recentlyDeletedMemories),
        userMemories: replaceMemory(action.memory, state.userMemories),
      }
    case galleryActionTypes.updateMemories: {
      let newState = { ...state }
      action.memories.forEach((memory) => {
        newState = {
          ...newState,
          archivedMemories: replaceMemory(memory, newState.archivedMemories),
          bookmarkedMemories: replaceMemory(memory, newState.bookmarkedMemories),
          recentlyDeletedMemories: replaceMemory(memory, newState.recentlyDeletedMemories),
          userMemories: replaceMemory(memory, newState.userMemories),
        }
      })
      return newState
    }
    default:
      return state
  }
}

const GalleryProvider = ({ children }) => {
  const isCurrent = useRef(true)
  const history = useHistory()
  const { cisId } = useEffectiveUser()
  const atSize = useContainerWidth()
  const isMobile = atSize({ default: true, xl: false })
  const actionsMenuOverlay = useMenuOverlay()
  const actionsBarContainerRef = useRef()
  const deleteMemoryOverlay = useOverlay()
  const reportAbuseOverlay = useOverlay()
  const {
    memories_enableRestoreDashboard: { isOn: enableRestoreDashboardFlag },
    memories_albumReorder: { isOn: albumReorderOn },
  } = useFeatureFlags(['memories_enableRestoreDashboard', 'memories_albumReorder'])

  const { getAllMissingMemories, getRecentlyDeletedMemories } = artifactManagerService
  const strgGalleryView = sessionStorage.getItem(sessionKeys.galleryView)

  const initialState = useMemo(
    () => ({
      actionsMenuOverlay,
      activePage: paths.gallery,
      archivedMemories: [],
      bookmarkedMemories: [],
      displayType: strgGalleryView === (undefined || null) ? displayTypes.grid : strgGalleryView,
      editing: false,
      memoriesDirty: false,
      memoriesErrors: [],
      memoriesLoading: true,
      missingMemories: [],
      pendingAction: false,
      recentlyDeletedMemories: [],
      selectedIds: [],
      selectMode: false,
      showExplorer: !isMobile,
      sortAscending: true,
      sortingType: sortingTypes.dateAdded,
      tombstones: [],
      userMemories: [],
    }),
    [actionsMenuOverlay, isMobile, strgGalleryView]
  )
  const [state, dispatch] = useReducer(galleryReducer, initialState)

  const selectedIdsCount = useMemo(() => state.selectedIds?.length ?? 0, [state.selectedIds])

  const getFilteredMemories = useCallback(
    (memories) => memories.filter((memory) => state.selectedIds.includes(memory.id)),
    [state.selectedIds]
  )

  const selectedMemories = useMemo(() => {
    const allMemories = [...state.userMemories, ...state.archivedMemories, ...state.bookmarkedMemories]

    let filteredMemories = getFilteredMemories(allMemories)
    if (filteredMemories.length === 0) {
      filteredMemories = getFilteredMemories(state.recentlyDeletedMemories)
    }

    const uniqueMemories = Array.from(new Set(filteredMemories.map((memory) => memory.id))).map((id) =>
      filteredMemories.find((memory) => memory.id === id)
    )

    return uniqueMemories
  }, [
    getFilteredMemories,
    state.archivedMemories,
    state.bookmarkedMemories,
    state.recentlyDeletedMemories,
    state.userMemories,
  ])

  const activeMemories = useMemo(() => {
    switch (state.activePage) {
      case paths.archive:
        return state.archivedMemories
      case paths.recentlyDeleted:
        return state.recentlyDeletedMemories
      case paths.bookmarks:
        return state.bookmarkedMemories
      // This needs to return an empty array to check for when to show the Image for empty collections in MemoriesContent
      // If you need the memories from an album, use albumMemories from useAlbums
      case paths.album:
        return []
      default:
        return state.userMemories
    }
  }, [
    state.activePage,
    state.archivedMemories,
    state.bookmarkedMemories,
    state.recentlyDeletedMemories,
    state.userMemories,
  ])

  const galleryActions = useMemo(
    () => ({
      init: ({
        archivedMemories,
        bookmarkedMemories,
        recentlyDeletedMemories,
        tombstones,
        userMemories,
        missingMemories,
        memoriesErrors,
      }) =>
        dispatch({
          type: galleryActionTypes.init,
          archivedMemories,
          bookmarkedMemories,
          recentlyDeletedMemories,
          tombstones,
          userMemories,
          missingMemories,
          memoriesErrors,
        }),
      addMemories: ({ memories }) => dispatch({ type: galleryActionTypes.addMemories, memories }),
      removeMemories: ({ memories }) => dispatch({ type: galleryActionTypes.removeMemories, memories }),
      setShowExplorer: (showExplorer) => dispatch({ type: galleryActionTypes.setShowExplorer, showExplorer }),
      setMemoriesErrors: (memoriesErrors) => dispatch({ type: galleryActionTypes.setMemoriesErrors, memoriesErrors }),
      setMemoriesLoading: (memoriesLoading) =>
        dispatch({ type: galleryActionTypes.setMemoriesLoading, memoriesLoading }),
      setActivePage: (path) => dispatch({ type: galleryActionTypes.setActivePage, path }),
      setDisplayType: (displayType) => dispatch({ type: galleryActionTypes.setDisplayType, displayType }),
      setMemoriesDirty: (memoriesDirty) => dispatch({ type: galleryActionTypes.setMemoriesDirty, memoriesDirty }),
      setSelectMode: (selectMode) => dispatch({ type: galleryActionTypes.setSelectMode, selectMode }),
      setSelectedIds: (selectedIds) => dispatch({ type: galleryActionTypes.setSelectedIds, selectedIds }),
      setSortingType: (sortingType) => dispatch({ type: galleryActionTypes.setSortingType, sortingType }),
      setSortAscending: (sortAscending) => dispatch({ type: galleryActionTypes.setSortAscending, sortAscending }),
      setArchivedMemories: (archivedMemories) =>
        dispatch({ type: galleryActionTypes.setArchivedMemories, archivedMemories }),
      setBookmarkedMemories: (bookmarkedMemories) =>
        dispatch({ type: galleryActionTypes.setBookmarkedMemories, bookmarkedMemories }),
      setPendingAction: (pendingAction) => dispatch({ type: galleryActionTypes.setPendingAction, pendingAction }),
      setRecentlyDeletedMemories: (recentlyDeletedMemories) =>
        dispatch({ type: galleryActionTypes.setRecentlyDeletedMemories, recentlyDeletedMemories }),
      setRestoredMemories: ({ tombstones, recentlyDeletedMemories, userMemories }) =>
        dispatch({
          type: galleryActionTypes.setRestoredMemories,
          tombstones,
          recentlyDeletedMemories,
          userMemories,
        }),
      setTombstones: (tombstones) => dispatch({ type: galleryActionTypes.setTombstones, tombstones }),
      setUserMemories: (userMemories) => dispatch({ type: galleryActionTypes.setUserMemories, userMemories }),
      updateMemories: (memories) => dispatch({ type: galleryActionTypes.updateMemories, memories }),
      updateMemory: (memory) => dispatch({ type: galleryActionTypes.updateMemory, memory }),
      beginEditing: () => dispatch({ type: galleryActionTypes.beginEditing }),
      endEditing: () => dispatch({ type: galleryActionTypes.endEditing }),
    }),
    []
  )

  const isMyMemoriesPage = useMemo(() => state.activePage === paths.gallery, [state.activePage])
  const isAlbumPage = useMemo(() => state.activePage === paths.album, [state.activePage])

  const galleryCallbacks = useMemo(
    () => ({
      archiveCallback: ({ archived, memory }) => {
        const memories = (Array.isArray(memory) ? memory : [memory]).map((m) => ({ ...m, archived: !archived }))
        const archivedIds = convertObjectsToIds(memories)
        if (archived) {
          galleryActions.setArchivedMemories(filterOutMemories(state.archivedMemories, archivedIds))
          galleryActions.setUserMemories([...state.userMemories, ...memories])
        } else {
          galleryActions.setArchivedMemories([...state.archivedMemories, ...memories])
          galleryActions.setUserMemories(filterOutMemories(state.userMemories, archivedIds))
        }
      },
      bookmarkCallback: ({ isBookmarked, memory }) => {
        if (isBookmarked) {
          galleryActions.setBookmarkedMemories(filterOutMemory(state.bookmarkedMemories, memory?.id))
        } else {
          galleryActions.setBookmarkedMemories([...state.bookmarkedMemories, memory])
        }
      },
      restoreCallback: ({ memory }) => {
        const memories = Array.isArray(memory) ? memory : [memory]
        const restoredIds = convertObjectsToIds(memories)
        // Set editableByCaller to true in state to show correct actions for restored memories without refreshing the page
        const updatedMemories = memories.map((memoryToRestore) => ({ ...memoryToRestore, editableByCaller: true }))
        // Remove tombstones from tombstones array
        const newTombstones = state.tombstones
        restoredIds.forEach((id) => delete newTombstones[id])
        galleryActions.setRestoredMemories({
          tombstones: newTombstones,
          recentlyDeletedMemories: filterOutMemories(state.recentlyDeletedMemories, restoredIds),
          userMemories: updatedMemories,
        })
      },
      expungeCallback: ({ memory }) => {
        const memories = Array.isArray(memory) ? memory : [memory]
        const expungedIds = convertObjectsToIds(memories)
        galleryActions.setRecentlyDeletedMemories(filterOutMemories(state.recentlyDeletedMemories, expungedIds))
      },
    }),
    [
      galleryActions,
      state.archivedMemories,
      state.bookmarkedMemories,
      state.recentlyDeletedMemories,
      state.tombstones,
      state.userMemories,
    ]
  )

  const fetchMemories = useCallback(async (apiParams) => {
    let errorItem = collections.memories
    let memories = []
    let memoryError = null

    if (apiParams?.archived) {
      errorItem = collections.archive
    } else if (apiParams?.bookmarked) {
      errorItem = collections.bookmarks
    }

    try {
      memories = (await getUserMemories(apiParams)) ?? []
    } catch (error) {
      memoryError = errorItem
    }
    return { memories, memoryError }
  }, [])

  const fetchRecentlyDeletedMemories = useCallback(async () => {
    let memories = []
    let memoryError = null
    let tombstones = {}
    try {
      const tombstoneData = (await getRecentlyDeletedMemories({ cisId }))?.data?.tombstones ?? []
      memories = tombstoneData.map((tombstone) => {
        const memory = tombstone?.artifactList?.[0] ?? {}
        memory.tombstoneId = tombstone?.id
        return memory
      })
      tombstones = tombstoneData.reduce((acc, memory) => {
        if (memory?.artifactList?.[0]?.id) {
          acc[memory?.artifactList?.[0]?.id] = memoryUtils.daysUntilDeleted(memory?.deletionDate)
        }
        return acc
      }, {})
    } catch (error) {
      memoryError = collections.deleted
    }
    return { memories, memoryError, tombstones }
  }, [cisId, getRecentlyDeletedMemories])

  const fetchMissingMemories = useCallback(async () => {
    let memories = []
    let memoryError = null
    if (enableRestoreDashboardFlag) {
      try {
        memories = (await getAllMissingMemories())?.data?.missing ?? []
      } catch (error) {
        memoryError = collections.missing
      }
    } else {
      memories = []
    }
    return { memories, memoryError }
  }, [enableRestoreDashboardFlag, getAllMissingMemories])

  useEffect(() => {
    const loadUserMemories = async () => {
      if (cisId) {
        galleryActions.setMemoriesLoading(true)

        const memoryCollectionTypes = {
          archivedMemories: 0,
          bookmarkedMemories: 1,
          userMemories: 2,
          missingMemories: 3,
          recentlyDeletedMemories: 4,
        }
        const memoriesResponses = await Promise.allSettled([
          fetchMemories({
            cisId,
            archived: true,
          }),
          fetchMemories({
            cisId,
            bookmarked: true,
          }),
          fetchMemories({ cisId }),
          fetchMissingMemories(),
          fetchRecentlyDeletedMemories(),
        ])

        const memoriesErrors = []
        if (isMemoryError(memoryCollectionTypes.archivedMemories, memoriesResponses))
          memoriesErrors.push(collections.archive)
        if (isMemoryError(memoryCollectionTypes.bookmarkedMemories, memoriesResponses))
          memoriesErrors.push(collections.bookmarks)
        if (isMemoryError(memoryCollectionTypes.userMemories, memoriesResponses))
          memoriesErrors.push(collections.missing)
        if (isMemoryError(memoryCollectionTypes.missingMemories, memoriesResponses))
          memoriesErrors.push(collections.deleted)
        if (isMemoryError(memoryCollectionTypes.recentlyDeletedMemories, memoriesResponses))
          memoriesErrors.push(collections.memories)

        isCurrent.current &&
          galleryActions.init({
            archivedMemories: memoriesResponses?.[memoryCollectionTypes.archivedMemories]?.value?.memories ?? [],
            bookmarkedMemories: memoriesResponses?.[memoryCollectionTypes.bookmarkedMemories]?.value?.memories ?? [],
            recentlyDeletedMemories:
              memoriesResponses?.[memoryCollectionTypes.recentlyDeletedMemories]?.value?.memories ?? [],
            tombstones: memoriesResponses?.[memoryCollectionTypes.recentlyDeletedMemories]?.value?.tombstones ?? {},
            userMemories: memoriesResponses?.[memoryCollectionTypes.userMemories]?.value?.memories ?? [],
            missingMemories: memoriesResponses?.[memoryCollectionTypes.missingMemories]?.value?.memories ?? [],
            memoriesErrors,
          })
      } else {
        galleryActions.setMemoriesLoading(false)
      }
    }
    loadUserMemories()
  }, [
    cisId,
    fetchMemories,
    fetchMissingMemories,
    fetchRecentlyDeletedMemories,
    galleryActions,
    getAllMissingMemories,
    getRecentlyDeletedMemories,
  ])

  // If the user shrinks their browser we want the explorer to be initially closed so it doesn't cover things up
  useEffect(() => {
    if (isMobile) {
      galleryActions.setShowExplorer(false)
    }
  }, [galleryActions, isMobile])

  /**
   * This sets the initial active page appropriately if the user goes directly to a url in the gallery.
   * This is why we only need the useEffect to run on the initial page load.
   * This will also set the sorting type initially. If we are on the album page we want to default to the custom order.
   */
  useEffect(() => {
    const pathname = history.location.pathname
    if (pathname) {
      galleryActions.setActivePage(pathname)
      galleryActions.setSortingType(
        pathname.includes('album') && albumReorderOn ? sortingTypes.custom : sortingTypes.dateAdded
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above comment
  }, [])

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  }, [])

  const apiValues = useMemo(
    () => ({ ...galleryActions, ...galleryCallbacks, fetchRecentlyDeletedMemories }),
    [galleryActions, galleryCallbacks, fetchRecentlyDeletedMemories]
  )

  const {
    activePage,
    archivedMemories,
    bookmarkedMemories,
    displayType,
    editing,
    memoriesDirty,
    memoriesErrors,
    memoriesLoading,
    missingMemories,
    pendingAction,
    recentlyDeletedMemories,
    selectedAlbum,
    selectedIds,
    selectMode,
    showExplorer,
    sortAscending,
    sortingType,
    tombstones,
    userMemories,
  } = state

  const dataValues = useMemo(
    () => ({
      actionsBarContainerRef,
      actionsMenuOverlay,
      activeMemories,
      activePage,
      archivedMemories,
      bookmarkedMemories,
      deleteMemoryOverlay,
      displayType,
      editing,
      isMobile,
      memoriesDirty,
      memoriesErrors,
      memoriesLoading,
      missingMemories,
      pendingAction,
      recentlyDeletedMemories,
      reportAbuseOverlay,
      selectedIds,
      selectedIdsCount,
      selectedMemories,
      selectMode,
      showExplorer,
      sortAscending,
      sortingType,
      tombstones,
      userMemories,
    }),
    [
      actionsMenuOverlay,
      activeMemories,
      activePage,
      archivedMemories,
      bookmarkedMemories,
      deleteMemoryOverlay,
      displayType,
      editing,
      isMobile,
      memoriesDirty,
      memoriesErrors,
      memoriesLoading,
      missingMemories,
      pendingAction,
      recentlyDeletedMemories,
      reportAbuseOverlay,
      selectedIds,
      selectedIdsCount,
      selectedMemories,
      selectMode,
      showExplorer,
      sortAscending,
      sortingType,
      tombstones,
      userMemories,
    ]
  )

  return (
    <GalleryContext.Provider value={dataValues}>
      <GalleryApiContext.Provider value={apiValues}>
        <AlbumsProvider
          galleryActions={galleryActions}
          memoriesLoading={memoriesLoading}
          isAlbumPage={isAlbumPage}
          isMobile={isMobile}
          isMyMemoriesPage={isMyMemoriesPage}
          sortAscending={sortAscending}
        >
          <MemoriesProviderWrapper
            memoriesDirty={memoriesDirty}
            memoriesLoading={memoriesLoading}
            galleryCallbacks={galleryCallbacks}
            galleryMemories={activeMemories}
            setMemoriesDirty={galleryActions.setMemoriesDirty}
          >
            {children}
            {reportAbuseOverlay.isOpen && (
              <ReportAbuseOverlay
                overlay={reportAbuseOverlay}
                subject={`Abuse reported for album ${selectedAlbum?.id}`}
              />
            )}
          </MemoriesProviderWrapper>
        </AlbumsProvider>
      </GalleryApiContext.Provider>
    </GalleryContext.Provider>
  )
}

export {
  displayTypes,
  collections,
  galleryActionTypes,
  GalleryApiContext,
  GalleryContext,
  GalleryProvider,
  sortingTypes,
}
